import { IBigcommerceAddress } from './bigcommerce-address';
import { IBigcommerceOrderProducts } from './bigcommerce-order-product';
import { IBigcommerceOrderShippingAddress } from './bigcommerce-shipping-address';

export class IBigcommerceOrder {
  /**
   * The ID of the order, a read-only value. Do not pass in PUT or POST.
   */
  id?: number;
  /**
   * The ID of the customer placing the order; or 0 if it was a guest order.
   */
  customer_id?: number;
  /**
   * The date this order was created. If not specified, will default to the current time. The date should be in RFC 2822 format.
   */
  date_created?: string;
  /**
   * A read-only value representing the last modification of the order. Do not attempt to modify or set this value in a POST or PUT operation. RFC-2822
   */
  date_modified?: string;
  /**
   * A read-only value representing the date of shipment. Do not attempt to modify or set this value in a POST or PUT operation. RFC-2822
   */
  date_shipped?: string;
  /**
   * The status ID of the order.
   */
  status_id?: number;
  /**
   * The cart ID from which this order originated, if applicable. Correlates with the Cart API. This is a READ-ONLY field; do not set or modify its value in a POST or PUT request.
   */
  cart_id?: string;
  /**
   * The status will include one of the (string, optiona) - values defined under Order Statuses. This value is read-only. Do not attempt to modify or set this value in a POST or PUT operation.
   */
  status?: string;
  /**
   * Contains the same (string, optiona) - value as the Order Statuses object's `custom_label` property.
   */
  custom_status?: string;
  /**
   * Override value for subtotal excluding tax. If specified, the field `subtotal_inc_tax` is also required. (Float, Float-As-String, Integer)
   */
  subtotal_ex_tax?: string;
  /**
   * Override value for subtotal including tax. If specified, the field `subtotal_ex_tax` is also required. (Float, Float-As-String, Integer)
   */
  subtotal_inc_tax?: string;
  /**
   * A read-only value. Do not attempt to set or modify this value in a POST or PUT operation. (Float, Float-As-String, Integer)
   */
  subtotal_tax?: string;
  /**
   * The value of the base shipping cost. (Float, Float-As-String, Integer)
   */
  base_shipping_cost?: string;
  /**
   * The value of shipping cost, excluding tax. (Float, Float-As-String, Integer)
   */
  shipping_cost_ex_tax?: string;
  /**
   * The value of shipping cost, including tax. (Float, Float-As-String, Integer)
   */
  shipping_cost_inc_tax?: string;
  /**
   * A read-only value. Do not attempt to modify or set this value in a POST or PUT operation. (Float, Float-As-String, Integer)
   */
  shipping_cost_tax?: string;
  /**
   * Shipping-cost tax class. A read-only value. Do not attempt to modify or set this value in a POST or PUT operation. (NOTE: Value ignored if automatic tax is enabled on the store.)
   */
  shipping_cost_tax_class_id?: number;
  /**
   * The value of the base handling cost. (Float, Float-As-String, Integer)
   */
  base_handling_cost?: string;
  /**
   * The value of the handling cost, excluding tax. (Float, Float-As-String, Integer)
   */
  handling_cost_ex_tax?: string;
  /**
   * The value of the handling cost, including tax. (Float, Float-As-String, Integer)
   */
  handling_cost_inc_tax?: string;
  /**
   * A read-only value. Do not attempt to modify or set this value in a POST or PUT operation. (Float, Float-As-String, Integer)
   */
  handling_cost_tax?: string;
  /**
   * A read-only value. Do not attempt to set or modify this value in a POST or PUT operation. (NOTE: Value ignored if automatic tax is enabled on the store.)
   */
  handling_cost_tax_class_id?: number;
  /**
   * The value of the base wrapping cost. (Float, Float-As-String, Integer)
   */
  base_wrapping_cost?: string;
  /**
   * The value of the wrapping cost, excluding tax. (Float, Float-As-String, Integer)
   */
  wrapping_cost_ex_tax?: string;
  /**
   * The value of the wrapping cost, including tax. (Float, Float-As-String, Integer)
   */
  wrapping_cost_inc_tax?: string;
  /**
   * A read-only value. Do not attempt to modify or set this value in a POST or PUT operation. (Float, Float-As-String, Integer)
   */
  wrapping_cost_tax?: string;
  /**
   * A read-only value. Do not attempt to set or modify this value in a POST or PUT operation. (NOTE: Value ignored if automatic tax is enabled on the store.)
   */
  wrapping_cost_tax_class_id?: number;
  /**
   * Override value for the total, excluding tax. If specified, the field `total_inc_tax` is also required. (Float, Float-As-String, Integer)
   */
  total_ex_tax?: string;
  /**
   * Override value for the total, including tax. If specified, the field `total_ex_tax` is also required. (Float, Float-As-String, Integer)
   */
  total_inc_tax?: string;
  /**
   * A read-only value. Do not attempt to set or modify this value in a POST or PUT operation. (Float, Float-As-String, Integer)
   */
  total_tax?: string;
  /**
   * The total number of items in the order.
   */
  items_total?: number;
  /**
   * The number of items that have been shipped.
   */
  items_shipped?: number;
  /**
   * The payment method for this order. Can be one of the following: `Manual`, `Credit Card`, `cash`, `Test Payment Gateway`, etc.
   */
  payment_method?: string;
  /**
   * The external Transaction ID/Payment ID within this order's payment provider (if a payment provider was used).
   */
  payment_provider_id?: string;
  /**
   * A read-only value. Do not attempt to set or modify this value in a POST or PUT operation.
   */
  payment_status?: string;
  /**
   * The amount refunded from this transaction. (Float, Float-As-String, Integer)
   */
  refunded_amount?: string;
  /**
   * Whether this is an order for digital products.
   */
  order_is_digital?: boolean;
  /**
   * Represents the store credit that the shopper has redeemed on this individual order. This is a read-only value. Do not pass in a POST or PUT. (Float, Float-As-String, Integer)
   */
  store_credit_amount?: string;
  /**
   * A read-only value. Do not pass in a POST or PUT. (Float, Float-As-String, Integer)
   */
  gift_certificate_amount?: string;
  /**
   * IP Address of the customer, if known.
   */
  ip_address?: string;
  /**
   * The full name of the country where the customer made the purchase, based on the IP.
   */
  geoip_country?: string;
  /**
   * The country where the customer made the purchase, in ISO2 format, based on the IP.
   */
  geoip_country_iso2?: string;
  /**
   * The ID of the currency being used in this transaction. A read-only value. Do not pass in a POST or PUT.
   */
  currency_id?: number;
  /**
   * The currency code of the currency being used in this transaction. A read-only value. Do not pass in a POST or PUT.
   */
  currency_code?: string;
  /**
   * A read-only value. Do not pass in a POST or PUT. (Float, Float-As-String, Integer)
   */
  currency_exchange_rate?: string;
  /**
   * A read-only value. Do not pass in a POST or PUT.
   */
  default_currency_id?: number;
  /**
   * The currency code of the default currency for this type of transaction. A read-only value. Do not pass in a POST or PUT.
   */
  default_currency_code?: string;
  /**
   * Any additional notes for staff.
   */
  staff_notes?: string;
  /**
   * Message that the customer entered (number, optiona) -o the `Order Comments` box during checkout.
   */
  customer_message?: string;
  /**
   * Amount of discount for this transaction. (Float, Float-As-String, Integer)
   */
  discount_amount?: string;
  /**
   * A read-only value. Do not pass in a POST or PUT. (Float, Float-As-String, Integer)
   */
  coupon_discount?: string;
  /**
   * The number of shipping addresses associated with this transaction. A read-only value. Do not pass in a POST or PUT.
   */
  shipping_address_count?: number;
  /**
   * Indicates whether the order was deleted (archived). Set to to true, to archive an order.
   */
  is_deleted?: boolean;
  /**
   * Indicates whether the shopper has selected an opt-in check box (on the checkout page) to receive emails. A read-only value. Do not pass in a POST or PUT.
   */
  is_email_opt_in?: boolean;
  /**
   * Credit card type
   */
  credit_card_type?: number;
  /**
   * If the order was placed through eBay, the eBay order number will be included. Otherwise, the value will be `0`.
   */
  ebay_order_id?: string;
  billing_address?: IBigcommerceAddress;
  /**
   * Orders submitted via the store's website will include a `www` value. Orders submitted via the API will be set to `external`. A read-only value. Do not pass in a POST or PUT.
   */
  order_source?: string;
  /**
   * For orders submitted or modified via the API, using a PUT or POST operation, you can optionally pass in a value identifying the system used to generate the order. For example: `POS`. Otherwise, the value will be null.
   */
  external_source?: string;
  products?: Products_Resource;
  // shipping_addresses?: ShippingAddress_Resource;
  coupons?: Coupons_Resource;
  /**
   * ID of the order in another system. For example, the Amazon Order ID if this is an Amazon order.This field can be updated in a /POST, but using a /PUT to update the order will return a 400 error. The field 'external_id' cannot be written to. Please remove it from your request before trying again. It can not be overwritten once set.
   */
  external_id?: string;
  /**
   * ID of the merchant
   */
  external_merchant_id?: string;
  /**
   * Shows where the order originated. The channel_id will default to 1.
   */
  channel_id?: number;
  /**
   * BasicTaxProvider - Tax is set to manual.
   *
   * AvaTaxProvider - This is for when the tax provider has been set to automatic and the order was NOT created by the API. Used for Avalara.
   *
   * "" (blank) - When the tax provider is unknown. This includes legacy orders and orders previously created via API.
   * This can be set when creating an order using a POST.
   *
   *
   */
  tax_provider_id?: 'BasicTaxProvider' | 'AvaTaxProvider' | '';
  [k: string]: any;
}
/**
 * Required to create an order.
 */
interface Products_Resource {
  url?: string;
  resource?: string;
  data?: IBigcommerceOrderProducts[];
  [k: string]: any;
}

interface ShippingAddress_Resource {
  /**
   * URL of the shipping address for api requests
   */
  url?: string;
  resource?: string;
  [k: string]: any;
  data: IBigcommerceOrderShippingAddress[];
}

interface Coupons_Resource {
  /**
   * URL of the coupons for api requests
   */
  url?: string;
  /**
   * resource of the coupons
   */
  resource?: string;
  [k: string]: any;
}
